import type { FormikProps } from 'formik'
import { Field, Formik } from 'formik'
import React from 'react'
import { Link } from 'react-router-dom'

import { Dashboard, InnerField, PageLayout, SelectedProducts } from '@components/common'
import { DashboardBackButton } from '@components/utils'
import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  Banner,
  ButtonClean,
  CurrencyInput,
  Fieldset,
  HeadContent,
  Icon,
  Input,
  InputUI,
  Pagination,
  RadioButton,
  Subsection,
  Switch,
  Table,
  Tag,
  Tooltip,
} from '@fourthwall/components'
import { QuestionMarkIcon } from '@fourthwall/icons'
import { pluralize } from '@fourthwall/utils/dist/string'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import {
  DURATION_MAX,
  DURATION_MIN,
  SettingsValidationSchema,
} from '@modules/Dashboard/pages/Apps/pages/AppsTwitch/consts'
import { routing } from '@utils/routing'

import { ConnectBar } from '../../components/ConnectBar/ConnectBar'
import { YouTubeVideoEntry } from '../../components/YouTubeVideoEntry/YouTubeVideoEntry'
import { mapStatusToTagProps } from '../../utils/mapStatusToTagProps'
import * as S from './AppsTwitch.styled'
import type { AppsTwitchViewProps, Values } from './AppsTwitch.types'

export const AppsTwitchView = ({
  formikRef,
  status,
  title,
  description,
  twitchConnect,
  streamelementsConnect,
  confirmBarProps,
  formProps,
  giftableProducts,
  giveaways,
}: AppsTwitchViewProps) => {
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  const getSelectedProductsError = (error?: string | string[]) => {
    if (!error || !error.length) return

    return typeof error === 'string' ? error : error[0]
  }

  const renderStatusTag = () => {
    const props = mapStatusToTagProps(status)

    if (!props) return null

    return (
      <div style={{ marginBottom: 20 }}>
        <Tag {...props} size="large" display="inline-flex" />
      </div>
    )
  }

  const renderTwitchConnect = () => {
    if (!twitchConnect || !twitchConnect.connectBar) return null

    return <ConnectBar {...twitchConnect.connectBar} />
  }

  const renderStreamElementsConnect = () => {
    if (!streamelementsConnect || !streamelementsConnect.connectBar) return null

    return (
      <>
        <ConnectBar {...streamelementsConnect.connectBar} />
        {streamelementsConnect.banner && <Banner {...streamelementsConnect.banner} />}
      </>
    )
  }

  const renderGiftingToggle = ({ values }: FormikProps<Values>) => (
    <Subsection>
      <Fieldset>
        <Fieldset.Row>
          <Field
            component={Switch}
            name="enabled"
            label={
              <S.SwitchLabelWrapper>
                Allow products to be gifted while I’m live on Twitch
              </S.SwitchLabelWrapper>
            }
            disabled={status !== 'CONNECTED' || values.platform === 'NOT_SELECTED'}
          />
        </Fieldset.Row>
      </Fieldset>
    </Subsection>
  )

  const renderChatbotSettings = ({ values, errors, dirty, submitCount }: FormikProps<Values>) => {
    if (!dashboardFeatureFlags.streamElementsGifting.enabled) return null

    const error =
      errors.platform && submitCount > 0 && dirty ? (
        <S.InputError>{errors.platform}</S.InputError>
      ) : null

    return (
      <Subsection title="Chatbot">
        <S.DescriptionWrapper>
          <p>
            Your giveaways will be announced and run by a chatbot in your channel. You can use the
            Fourthwall chatbot or the StreamElements chatbot.
          </p>
        </S.DescriptionWrapper>
        <Fieldset>
          <Fieldset.Row>
            <Field
              component={RadioButton}
              name="platform"
              label="Use Fourthwall chatbot"
              checkedValue="TWITCH"
            />
          </Fieldset.Row>
          {values.platform === 'TWITCH' && (
            <S.ConnectBarWrapper>
              {renderTwitchConnect()}
              {error}
            </S.ConnectBarWrapper>
          )}
          <Fieldset.Row>
            <Field
              component={RadioButton}
              name="platform"
              label="Use StreamElements chatbot"
              checkedValue="STREAMELEMENTS"
            />
          </Fieldset.Row>
          {values.platform === 'STREAMELEMENTS' && (
            <S.ConnectBarWrapper>
              {renderStreamElementsConnect()}
              {error}
            </S.ConnectBarWrapper>
          )}
          {dashboardFeatureFlags.developersCustomChatbot.enabled && (
            <Fieldset.Row>
              <Field
                component={RadioButton}
                name="platform"
                label={
                  <S.CustomChatbotLabelWrapper>
                    Use custom chatbot
                    <Tooltip reference={<Icon component={QuestionMarkIcon} height={16} />}>
                      A custom chatbot allows you to use your own chatbot to announce and run
                      giveaways. See docs.fourthwall.com for more information.
                    </Tooltip>
                  </S.CustomChatbotLabelWrapper>
                }
                checkedValue="OPEN_API"
              />
            </Fieldset.Row>
          )}
        </Fieldset>
      </Subsection>
    )
  }

  const renderEntryTimer = () => {
    if (status !== 'CONNECTED') return null

    return (
      <Subsection title="Giveaway entry time limit">
        <Fieldset>
          <Fieldset.Row>
            <div>
              <S.NumberInputWrapper>
                <Field
                  component={Input}
                  name="duration"
                  label="Entry time limit (sec)"
                  type="number"
                  hideSpinner
                  displayError={false}
                  min={DURATION_MIN}
                  max={DURATION_MAX}
                />
              </S.NumberInputWrapper>
              <InputUI.Suggestion>
                Set the amount of time viewers have to enter giveaways ({DURATION_MIN}-
                {DURATION_MAX} secs).
              </InputUI.Suggestion>
            </div>
          </Fieldset.Row>
        </Fieldset>
      </Subsection>
    )
  }

  const renderInternationalShipping = ({ values }: FormikProps<Values>) => {
    if (status !== 'CONNECTED') return null

    return (
      <Subsection title="Covering the cost of international shipping">
        <S.DescriptionWrapper>
          <p>
            The average cost of domestic shipping is included in the gift price, but international
            shipping may cost extra. How would you like to handle international shipping costs?
          </p>
        </S.DescriptionWrapper>
        <Fieldset>
          <Fieldset.Row>
            <Field
              component={RadioButton}
              name="shippingChargeType"
              label="Ask gift winner to cover extra cost"
              checkedValue="ALL_WINNER"
            />
          </Fieldset.Row>
          <Fieldset.Row>
            <Field
              component={RadioButton}
              name="shippingChargeType"
              label="Cover extra cost myself"
              checkedValue="ALL_CREATOR"
            />
          </Fieldset.Row>
          <Fieldset.Row>
            <Field
              component={RadioButton}
              name="shippingChargeType"
              label="Cover up to a specific amount, with any remaining costs covered by the gift winner"
              checkedValue="MAX_CREATOR"
            />
          </Fieldset.Row>
        </Fieldset>
        {values.shippingChargeType === 'MAX_CREATOR' && (
          <InnerField fluid>
            <S.NumberInputWrapper>
              <Field
                component={CurrencyInput}
                name="shippingCharge"
                label="Covered by me"
                type="number"
                hideSpinner
                min={DURATION_MIN}
                max={DURATION_MAX}
              />
            </S.NumberInputWrapper>
          </InnerField>
        )}
      </Subsection>
    )
  }

  const renderGiftableProducts = ({ values, submitCount, errors }: FormikProps<Values>) => {
    if (status !== 'CONNECTED') return null

    const { productSelectionType, excludedProductsIds } = values

    return (
      <Subsection title="Giftable products">
        <Fieldset>
          <Fieldset.Row>
            <Field
              component={RadioButton}
              name="productSelectionType"
              label={
                <div>
                  {/* Make sure it's wrapped in div element to avoid flex styles. */}
                  Make all products giftable while I’m livestreaming
                  {productSelectionType === 'ALL_PRODUCTS' && !excludedProductsIds.length && (
                    <>
                      <br />
                      <ButtonClean
                        appearance="secondary"
                        label="Exclude selected products"
                        onClick={() => giftableProducts.excludedProducts.onAddProduct()}
                      />
                    </>
                  )}
                </div>
              }
              checkedValue="ALL_PRODUCTS"
            />
          </Fieldset.Row>
          {values.productSelectionType === 'ALL_PRODUCTS' &&
            !!values.excludedProductsIds.length && (
              <InnerField fluid>
                <SelectedProducts
                  maxHeight={500}
                  header={
                    <>
                      {values.excludedProductsIds.length}{' '}
                      {pluralize(values.excludedProductsIds.length, 'product')} excluded
                    </>
                  }
                  isLoading={giftableProducts.excludedProducts.isLoading}
                  productsCount={values.excludedProductsIds.length}
                  data={giftableProducts.excludedProducts.data}
                  onAddProducts={giftableProducts.excludedProducts.onAddProduct}
                  onRemoveProduct={giftableProducts.excludedProducts.onRemoveProduct}
                />
              </InnerField>
            )}
          <Fieldset.Row>
            <Field
              component={RadioButton}
              name="productSelectionType"
              label="Make selected products giftable while I’m livestreaming"
              checkedValue="SELECTED_PRODUCTS"
            />
          </Fieldset.Row>
        </Fieldset>
        {values.productSelectionType === 'SELECTED_PRODUCTS' && (
          <InnerField fluid>
            <SelectedProducts
              maxHeight={500}
              isLoading={giftableProducts.includedProducts.isLoading}
              productsCount={values.includedProductsIds.length}
              data={giftableProducts.includedProducts.data}
              error={
                !submitCount ? undefined : getSelectedProductsError(errors.includedProductsIds)
              }
              onAddProducts={giftableProducts.includedProducts.onAddProduct}
              onRemoveProduct={giftableProducts.includedProducts.onRemoveProduct}
            />
          </InnerField>
        )}
      </Subsection>
    )
  }

  const renderForm = () => {
    if (!formProps) return null

    return (
      <Formik<Values>
        innerRef={formikRef}
        enableReinitialize
        validationSchema={SettingsValidationSchema}
        {...formProps}
      >
        {(formikProps) => (
          <>
            <Subsection>
              {renderGiftingToggle(formikProps)}
              {status && (
                <div style={{ marginBlock: 20 }}>
                  <YouTubeVideoEntry
                    title="How Twitch Gifting Works"
                    description="See how to let your viewers gift merch during your Twitch streams and trigger live giveaways with just a few clicks."
                    size={status === 'NOT_CONNECTED' ? 'regular' : 'compact'}
                    videoId="yuRO2b2hzuU"
                  />
                </div>
              )}
            </Subsection>
            {renderChatbotSettings(formikProps)}
            {renderEntryTimer()}
            {renderInternationalShipping(formikProps)}
            {renderGiftableProducts(formikProps)}
            <ConfirmBar
              isOpen={formikProps.dirty}
              onConfirm={formikProps.submitForm}
              isLoading={confirmBarProps.isLoading}
              onCancel={() => {
                formikProps.resetForm()
                confirmBarProps.onCancel()
              }}
            />
            <LeavingGuard
              when={formikProps.dirty}
              isValid={formikProps.isValid}
              onSave={formikProps.submitForm}
            />
          </>
        )}
      </Formik>
    )
  }

  const renderPurchasedGifts = () => {
    if (!giveaways) return null

    return (
      <Subsection title="Purchased gifts" borderless>
        <Dashboard.Section.Item>
          <Table
            {...giveaways.table}
            emptyMessage="There are no purchased gifts yet"
            rowComponent={Link}
            minWidth={0}
          />
        </Dashboard.Section.Item>
        {giveaways.pagination && (
          <Dashboard.Section.Item>
            <Pagination {...giveaways.pagination} />
          </Dashboard.Section.Item>
        )}
      </Subsection>
    )
  }

  return (
    <>
      <HeadContent title={title} />
      <PageLayout size="tiny">
        <Dashboard.Section
          name={title}
          topContent={<DashboardBackButton label="Apps" to={routing.apps.self} />}
        >
          <Subsection>
            <S.HeaderContainer>
              {renderStatusTag()}
              <S.DescriptionWrapper>
                <p>{description}</p>
              </S.DescriptionWrapper>
              {!dashboardFeatureFlags.streamElementsGifting.enabled && (
                <div>{renderTwitchConnect()}</div>
              )}
            </S.HeaderContainer>
            {renderForm()}
          </Subsection>
          {renderPurchasedGifts()}
        </Dashboard.Section>
      </PageLayout>
    </>
  )
}
